import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { CONTENT_STATUS, TASK_STATUS, label } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Megaphone, ListChecks, Send, Camera, Play, Check, RotateCcw } from "lucide-react";
import { PendingWorkTable } from "@/components/PendingWorkTable";
import { InProgressNow } from "@/components/InProgressNow";
import { UpcomingFestivals } from "@/components/UpcomingFestivals";
import { toast } from "sonner";

export default function MyWork() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["my-work"], queryFn: async () => (await api.get("/my-work")).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["my-work"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };
  const setContent = async (id, status) => { await api.put(`/content/${id}`, { status }); toast.success(`Marked ${status.replace("_", " ")}`); refresh(); };
  const setTask = async (id, status) => { await api.put(`/tasks/${id}`, { status }); toast.success(`Task ${status.replace("_", " ")}`); refresh(); };

  if (isLoading || !data)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  return (
    <div className="space-y-6" data-testid="mywork-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">My Work Today</h1>
        <p className="mt-1 text-sm text-zinc-500">Hi {user?.name?.split(" ")[0]} — your content and tasks, kept separate.</p>
      </div>

      <InProgressNow />

      <UpcomingFestivals days={45} />

      <PendingWorkTable />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="rounded-lg border-zinc-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4">
            <Megaphone className="h-4 w-4 text-zinc-400" /><h3 className="font-heading text-base font-semibold">My Content Work</h3>
            <Badge color="blue">{data.content.length}</Badge>
          </div>
          <div className="space-y-2 p-5">
            {data.content.length === 0 && <div className="py-4 text-center text-sm text-zinc-400">No content assigned</div>}
            {data.content.map((c) => (
              <div key={c.id} className="rounded-md border border-zinc-100 p-3" data-testid={`mywork-content-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><div className="text-sm font-medium">{c.title}</div><div className="text-xs text-zinc-500">{c.client_name} · {fmtDate(c.date)}</div></div>
                  <Badge color={CONTENT_STATUS[c.status]?.color}>{label(CONTENT_STATUS, c.status)}</Badge>
                </div>
                <div className="mt-2 flex gap-2">
                  {c.status === "planned" && <Button size="sm" variant="outline" onClick={() => setContent(c.id, "in_progress")}><Play className="mr-1 h-3 w-3" />Start Work</Button>}
                  {c.status === "in_progress" && ((c.started_by_id === user?.id || user?.role === "super_admin" || user?.role === "manager")
                    ? <><Button size="sm" variant="outline" onClick={() => setContent(c.id, "ready_for_approval")}><Check className="mr-1 h-3 w-3" />Mark Completed</Button><Button size="sm" variant="ghost" className="px-2 text-zinc-400" title="Undo start" onClick={() => setContent(c.id, "planned")}><RotateCcw className="h-3.5 w-3.5" /></Button></>
                    : <span className="text-xs italic text-zinc-400">In progress · {c.started_by || "someone"}</span>)}
                  {c.status === "ready_for_approval" && <><span className="text-xs text-blue-500">Ready for approval</span>{(c.completed_by_id === user?.id || user?.role === "super_admin" || user?.role === "manager") && <Button size="sm" variant="ghost" className="px-2 text-zinc-400" title="Undo" onClick={() => setContent(c.id, "in_progress")}><RotateCcw className="h-3.5 w-3.5" /></Button>}</>}
                  {c.status === "approved" && <span className="text-xs text-zinc-400">Ready for publishing</span>}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-lg border-zinc-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4">
            <ListChecks className="h-4 w-4 text-zinc-400" /><h3 className="font-heading text-base font-semibold">My General Tasks</h3>
            <Badge color="orange">{data.tasks.length}</Badge>
          </div>
          <div className="space-y-2 p-5">
            {data.tasks.length === 0 && <div className="py-4 text-center text-sm text-zinc-400">No tasks assigned</div>}
            {data.tasks.map((t) => (
              <div key={t.id} className="rounded-md border border-zinc-100 p-3" data-testid={`mywork-task-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div><div className="text-sm font-medium">{t.title}</div><div className="text-xs text-zinc-500">Due {fmtDate(t.due_date)}{t.client_name ? ` · ${t.client_name}` : ""}</div></div>
                  <Badge color={TASK_STATUS[t.display_status]?.color}>{label(TASK_STATUS, t.display_status)}</Badge>
                </div>
                <div className="mt-2 flex gap-2">
                  {t.status === "todo" && <Button size="sm" variant="outline" onClick={() => setTask(t.id, "in_progress")}><Play className="mr-1 h-3 w-3" />Start</Button>}
                  {t.status !== "completed" && <Button size="sm" variant="outline" onClick={() => setTask(t.id, "completed")}><Check className="mr-1 h-3 w-3" />Complete</Button>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="rounded-lg border-zinc-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
            <div className="flex items-center gap-2"><Send className="h-4 w-4 text-zinc-400" /><h3 className="font-heading text-base font-semibold">To Publish</h3><Badge color="green">{data.to_publish.length}</Badge></div>
            <button onClick={() => navigate("/publishing")} className="text-xs font-medium text-zinc-500 hover:text-zinc-900">Open Publishing →</button>
          </div>
          <div className="space-y-2 p-5">
            {data.to_publish.length === 0 && <div className="py-4 text-center text-sm text-zinc-400">Nothing approved for posting</div>}
            {data.to_publish.slice(0, 6).map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-md border border-zinc-100 px-3 py-2">
                <div className="text-sm"><span className="font-medium">{c.title}</span> <span className="text-zinc-400">· {c.client_name}</span></div>
                <Button size="sm" onClick={() => setContent(c.id, "posted")} data-testid={`publish-${c.id}`}>Mark Posted</Button>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-lg border-zinc-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4"><Camera className="h-4 w-4 text-zinc-400" /><h3 className="font-heading text-base font-semibold">My Shoots</h3><Badge color="blue">{data.shoots.length}</Badge></div>
          <div className="space-y-2 p-5">
            {data.shoots.length === 0 && <div className="py-4 text-center text-sm text-zinc-400">No upcoming shoots</div>}
            {data.shoots.map((sh) => (
              <div key={sh.id} className="flex items-center justify-between rounded-md border border-zinc-100 px-3 py-2">
                <div><div className="text-sm font-medium">{sh.shoot_type}</div><div className="text-xs text-zinc-500">{sh.client_name} · {fmtDate(sh.date)} · {sh.start_time}</div></div>
                <Button size="sm" variant="outline" onClick={() => navigate("/shoots")}>View</Button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
